/** Native source panel: exact highlighted answer, its ordered exchange and the saved profile supplied by Web. */
import type { ShopifyResultQuote } from '@testfeed/shopify-contracts'

type Exchange = NonNullable<ShopifyResultQuote['originalExchange']>
type Answer = Exchange['answers'][number]
type Profile = NonNullable<Exchange['profile']>

export function ServerResultSourceModal({
  id,
  exchange,
  selected,
}: {
  id: string
  exchange: Exchange
  selected: Answer | null
}) {
  return (
    <s-modal id={id} heading={exchange.source} size="large">
      <SourceContent exchange={exchange} selected={selected} />
      <s-button slot="secondary-actions" commandFor={id} command="--hide">
        {exchange.sourceLabels.close}
      </s-button>
    </s-modal>
  )
}

function SourceContent({ exchange, selected }: { exchange: Exchange; selected: Answer | null }) {
  if (!selected) return null
  return (
    <s-stack direction="block" gap="large">
      <s-heading>{selected.originLabel ?? exchange.sourceLabels.answer}</s-heading>
      <s-text type="strong">{selected.question}</s-text>
      <SourceAnswerText answer={selected} />
      <details className="response-phrase-disclosure">
        <summary>{exchange.sourceLabels.conversation}</summary>
        <s-stack direction="block" gap="large">
          {exchange.answers.map((answer) => (
            <s-stack direction="block" gap="small" key={answer.id}>
              <s-text color="subdued">{answer.originLabel}</s-text>
              <s-text type="strong">{answer.question}</s-text>
              <s-paragraph>{answer.response}</s-paragraph>
            </s-stack>
          ))}
        </s-stack>
      </details>
      <details className="response-phrase-disclosure">
        <summary>{exchange.sourceLabels.profile}</summary>
        <SavedProfile profile={exchange.profile} labels={exchange.sourceLabels} />
      </details>
    </s-stack>
  )
}

export function SourceAnswerText({ answer }: { answer: Answer }) {
  return (
    <p className="response-phrase-quote">
      {answer.segments.map((segment, index) => (
        <SourceSegment key={index} segment={segment} />
      ))}
    </p>
  )
}
function SourceSegment({ segment }: { segment: Answer['segments'][number] }) {
  if (segment.highlighted) return <mark>{segment.text}</mark>
  return <>{segment.text}</>
}

function SavedProfile({ profile, labels }: { profile: Profile | null; labels: Exchange['sourceLabels'] }) {
  if (!profile) return <s-paragraph color="subdued">{labels.unavailableProfile}</s-paragraph>
  return (
    <s-stack direction="block" gap="large">
      <s-heading>{profile.heading}</s-heading>
      <s-text type="strong">{profile.name}</s-text>
      <s-paragraph color="subdued">{profile.notice}</s-paragraph>
      <ProfileFields title={labels.details} fields={profile.fields} />
      <ProfileFields title={labels.context} fields={profile.context} />
      <ProfileProvenance title={labels.provenance} entries={profile.provenance} />
      <ProfileLimitations title={labels.limitations} limitations={profile.limitations} />
    </s-stack>
  )
}
function ProfileFields({ title, fields }: { title: string; fields: Profile['fields'] }) {
  if (!fields.length) return null
  return (
    <s-stack direction="block" gap="base">
      <s-heading>{title}</s-heading>
      {fields.map((field, index) => (
        <s-stack direction="block" gap="small" key={index}>
          <s-text type="strong">{field.label}</s-text>
          <s-paragraph>{field.value}</s-paragraph>
          <RecordedSource text={field.source} />
        </s-stack>
      ))}
    </s-stack>
  )
}
function RecordedSource({ text }: { text: string | null }) {
  if (!text) return null
  return <s-text color="subdued">{text}</s-text>
}
function ProfileProvenance({ title, entries }: { title: string; entries: Profile['provenance'] }) {
  if (!entries.length) return null
  return (
    <details className="response-phrase-disclosure">
      <summary>{title}</summary>
      <s-stack direction="block" gap="base">
        {entries.map((entry, index) => (
          <s-stack direction="block" gap="small" key={index}>
            <s-text type="strong">{entry.label}</s-text>
            <s-paragraph>{entry.value}</s-paragraph>
          </s-stack>
        ))}
      </s-stack>
    </details>
  )
}
function ProfileLimitations({ title, limitations }: { title: string; limitations: string[] }) {
  if (!limitations.length) return null
  return (
    <s-stack direction="block" gap="base">
      <s-heading>{title}</s-heading>
      {limitations.map((limitation, index) => (
        <s-paragraph key={index}>{limitation}</s-paragraph>
      ))}
    </s-stack>
  )
}
